import * as React from 'react';
import classnames from 'classnames';

// constants
import {
    AGE_RATINGS_COUNTRY_RULES,
    AGE_RATINGS_LINKS,
    ESRB_PRIVACY_RULES,
    ESRB_PRIVACY_LINK,
    DEFAULT_FALLBACK_RATING,
    AGE_RATINGS_ANALYTICS_ATTRIBUTES
} from './age-ratings.constants';
import AnalyticsService from 'modules/analytics-provider/analytics-provider.service';

// types
import { IModule } from '@sitegen/common/types';
import {
    IAgeRatingProps,
    IAgeRatingState,
    IRating
} from './age-ratings.types';

import './age-ratings.styles.less';

export class AgeRatings extends React.Component<IAgeRatingProps, IAgeRatingState> {
    constructor(props: IAgeRatingProps) {
        super(props);
        this.state = {
            ratings: [],
            showESRBPrivacy: false,
            ESRBPrivacyIconURL: ''
        };
        this.setupRatings();
    }

    public getRatingIcon = (iconName: string): Promise<string> =>
        import(`./assets/${iconName.toLowerCase()}.png`)
            .then((pngModule: IModule): string => pngModule.default);

    public setPrivacyIconUrl = (showPrivacy: boolean): Promise<string> =>
        new Promise<string>(
            resolve => showPrivacy ? this.getRatingIcon('esrb_po').then(resolve) : resolve('')
        );

    public setupRatings = async () => {
        const downloadIcons: Promise<IRating>[] = [];

        const legalRatings = Object.keys(AGE_RATINGS_COUNTRY_RULES).filter(ratingItem =>
            AGE_RATINGS_COUNTRY_RULES[ratingItem].includes(this.props.countryCode)
        );

        if (!legalRatings.length) {
            legalRatings.push(DEFAULT_FALLBACK_RATING);
        }

        Object.entries(this.props.ratings).forEach(
            (rating: [string, string]): void => {
                const [ratingName, ratingValue] = rating;

                if (legalRatings.includes(ratingName) && !!ratingValue) {
                    downloadIcons.push(
                        this.getRatingIcon(ratingValue).then((iconURL: string) => ({
                            rating: ratingName,
                            value: ratingValue,
                            iconURL: iconURL
                        }))
                    );
                }
            }
        );

        await Promise.all<IRating>(downloadIcons).then(fullfilledRatings => {
            const showESRBPrivacy = !!fullfilledRatings.find(item => ESRB_PRIVACY_RULES.includes(item.rating));
            this.setPrivacyIconUrl(showESRBPrivacy).then(url => {
                this.setState(prevState => ({
                    ...prevState,
                    showESRBPrivacy: showESRBPrivacy,
                    ESRBPrivacyIconURL: url,
                    ratings: fullfilledRatings
                }));
            });
        });
    };

    public handleLinkClick = (): void => {
        AnalyticsService.sendClickEvent(AGE_RATINGS_ANALYTICS_ATTRIBUTES);
    };

    public render() {
        const { ratings, ESRBPrivacyIconURL, showESRBPrivacy } = this.state;
        const totalLinkCount = ratings.length + (showESRBPrivacy ? 1 : 0);

        return (
            <div
                className={classnames('sitegen-age-ratings-logo-box', {
                    'without-border': totalLinkCount === 0,
                    'space-between': totalLinkCount > 1
                })}
            >
                {ratings.map(ratingItem => (
                    <a
                        key={ratingItem.rating}
                        href={AGE_RATINGS_LINKS[ratingItem.rating]}
                        target="_blank"
                        rel="noreferrer"
                        onClick={this.handleLinkClick}
                    >
                        <img src={ratingItem.iconURL} alt={ratingItem.rating} />
                    </a>
                ))}
                {showESRBPrivacy && (
                    <a
                        href={ESRB_PRIVACY_LINK}
                        target="_blank"
                        rel="noreferrer"
                        onClick={this.handleLinkClick}
                    >
                        <img src={ESRBPrivacyIconURL} alt="ESRB Privacy Online" />
                    </a>
                )}
            </div>
        );
    }
}

export default AgeRatings;
